import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Send } from "lucide-react";
import { Reveal, MaskedLine } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIMS = [
  { value: "2026-09-13", label: "Sept 13" },
  { value: "2026-10-25", label: "Oct 25" },
];
const DIVISIONS = [
  { value: "all", label: "All" },
  { value: "singles", label: "Singles" },
  { value: "doubles", label: "Doubles" },
];

const fmt = (s) =>
  `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const EMPTY_FORM = { name: "", sim_date: "2026-09-13", division: "singles", category: "men", time: "" };

export default function Results() {
  const [sim, setSim] = useState("2026-09-13");
  const [division, setDivision] = useState("all");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/results`, { params: { sim_date: sim, division } });
      setRows(data);
    } catch {
      setRows([]);
    }
  }, [sim, division]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/results`, form);
      toast.success("Time posted. Respect.");
      setSim(form.sim_date);
      setForm({ ...EMPTY_FORM, sim_date: form.sim_date });
      if (form.sim_date === sim) load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post your time — check the fields.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="results-page" className="pt-16">
      <Seo
        title="HYROX Simulation Leaderboard — Dallas | HyroxNinja"
        description="Official race results board for the Hyrox Ninja full HYROX simulations in Dallas. Post your finish time after sim day and see where you stack up — singles and doubles."
      />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-12">
        <p className="eyebrow mb-4">Race Results Board</p>
        <h1 className="font-head font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl mb-6">
          <MaskedLine delay={0.1}>The Simulation</MaskedLine>
          <MaskedLine delay={0.25} className="text-volt">Leaderboard</MaskedLine>
        </h1>
        <p className="text-zinc-400 max-w-2xl leading-relaxed">
          Finished a full simulation? Post your time and claim your spot. Times
          are community-reported — hold the standard, post the real number.
        </p>
        <p className="text-zinc-500 text-xs leading-relaxed mt-4" data-testid="results-blog-link">
          How to read your result:{" "}
          <Link to="/blog/dallas-hyrox-simulation-results" className="text-volt hover:text-white transition-colors underline underline-offset-2">Dallas sim results & recaps</Link>.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24 grid lg:grid-cols-3 gap-8 items-start">
        {/* POST FORM */}
        <Reveal className="lg:sticky lg:top-24">
          <form onSubmit={submit} className="border border-white/10 bg-[#0c0c0c] p-7" data-testid="result-form">
            <p className="eyebrow mb-4 flex items-center gap-2"><Send size={13} /> Post Your Time</p>
            <label className="block mb-4">
              <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">Athlete / Team Name</span>
              <input required value={form.name} onChange={set("name")} placeholder="e.g. ALEX R. or ALEX & JORDAN" className="volt-input" data-testid="result-name-input" />
            </label>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <label className="block">
                <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">Sim Day</span>
                <select value={form.sim_date} onChange={set("sim_date")} className="volt-input" data-testid="result-sim-select">
                  {SIMS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">Division</span>
                <select value={form.division} onChange={set("division")} className="volt-input" data-testid="result-division-select">
                  <option value="singles">Singles</option>
                  <option value="doubles">Doubles</option>
                </select>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-6">
              <label className="block">
                <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">Category</span>
                <select value={form.category} onChange={set("category")} className="volt-input" data-testid="result-category-select">
                  <option value="men">Men</option>
                  <option value="women">Women</option>
                  <option value="mixed">Mixed</option>
                </select>
              </label>
              <label className="block">
                <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">Finish Time</span>
                <input required value={form.time} onChange={set("time")} placeholder="1:17:30 or 77:30" className="volt-input" data-testid="result-time-input" />
              </label>
            </div>
            <button type="submit" disabled={busy} className="btn-volt w-full justify-center disabled:opacity-60" data-testid="result-submit-button">
              {busy ? "Posting..." : "Post to the Board"}
            </button>
          </form>
        </Reveal>

        {/* BOARD */}
        <div className="lg:col-span-2">
          <Reveal>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="flex gap-2" data-testid="leaderboard-sim-tabs">
                {SIMS.map((s) => (
                  <button key={s.value} onClick={() => setSim(s.value)} data-testid={`sim-tab-${s.value}`}
                    className={`font-head uppercase text-lg font-extrabold px-5 py-2 border transition-colors ${
                      sim === s.value ? "bg-volt text-black border-volt" : "border-white/20 text-zinc-300 hover:border-white/50"
                    }`}>
                    {s.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-1" data-testid="leaderboard-division-filter">
                {DIVISIONS.map((d) => (
                  <button key={d.value} onClick={() => setDivision(d.value)} data-testid={`division-filter-${d.value}`}
                    className={`font-mono2 text-[11px] uppercase tracking-[0.15em] px-3 py-2 border transition-colors ${
                      division === d.value ? "border-volt text-volt" : "border-white/15 text-zinc-400 hover:text-white"
                    }`}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="border border-white/10" data-testid="leaderboard-table">
              <div className="grid grid-cols-12 gap-2 px-5 py-3 bg-[#0c0c0c] border-b border-white/10 font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                <span className="col-span-1">#</span>
                <span className="col-span-5">Athlete</span>
                <span className="col-span-2">Division</span>
                <span className="col-span-2">Category</span>
                <span className="col-span-2 text-right">Time</span>
              </div>
              {rows.length === 0 && (
                <div className="px-5 py-14 text-center" data-testid="leaderboard-empty">
                  <Trophy size={28} className="text-zinc-700 mx-auto mb-3" />
                  <p className="font-head uppercase text-xl text-zinc-500">No times on the board yet</p>
                  <p className="text-zinc-600 text-sm mt-1">Race the sim, then be the first to post.</p>
                </div>
              )}
              {rows.map((r, i) => (
                <div key={r.id} data-testid={`leaderboard-row-${i}`}
                  className={`grid grid-cols-12 gap-2 px-5 py-4 items-center border-b border-white/5 last:border-b-0 ${i < 3 ? "bg-volt/5" : ""} hover:bg-white/5 transition-colors`}>
                  <span className={`col-span-1 font-head font-black text-2xl ${i === 0 ? "text-volt" : i < 3 ? "text-white" : "text-zinc-600"}`}>
                    {i + 1}
                  </span>
                  <span className="col-span-5 font-head font-bold uppercase text-lg truncate">{r.name}</span>
                  <span className="col-span-2 font-mono2 text-[11px] uppercase tracking-wider text-zinc-400">{r.division}</span>
                  <span className="col-span-2 font-mono2 text-[11px] uppercase tracking-wider text-zinc-400">{r.category}</span>
                  <span className={`col-span-2 text-right font-head font-black text-xl tabular-nums ${i === 0 ? "text-volt" : "text-white"}`}>
                    {fmt(r.seconds)}
                  </span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
